import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/about")({
  component: AboutPage,
});

function AboutPage() {
  return (
    <article className="essay mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12 prose prose-ink">
      <header className="mb-10">
        <div className="eyebrow mb-3">Essay · Lingua engine</div>
        <h1 className="font-serif text-4xl text-ink-900 mb-3 tracking-tight">
          How lingua works
        </h1>
        <p className="text-ink-500 text-lg">
          The adaptive engine behind the practice page, in plain English.
        </p>
        <div className="hairline mt-6" />
      </header>

      <Section title="In one paragraph">
        <p>
          For every skill (e.g. <em>accusative case</em>) and every word (e.g.{" "}
          <em>Kind</em>), the app keeps a number called{" "}
          <strong>stability</strong> — how many days you'd hold onto that memory
          before it starts decaying. A right answer grows that number, a wrong
          one shrinks it. Whenever it predicts that your memory probability on
          something has dropped to about 85%, that something becomes "due" and
          the scheduler surfaces it. The scheduler picks <em>which</em> due
          thing to test by asking which one would teach the model the most about
          you right now (highest information gain). That's the whole loop.
        </p>
      </Section>

      <Section title="Bootstrap mode — why some exercise types are hidden">
        <p>
          You may notice the amber <strong>"bootstrap mode"</strong> banner at
          the top of every page. When it's on, the practice queue is
          intentionally restricted to{" "}
          <strong>discrete-choice questions only</strong> — true/false,
          multiple-choice, odd-one-out, and anything (fill-blanks,
          paradigm-fill, word-formation) that we can render with a fixed option
          list at scaffolding level ≤ 1. Free-text formats — translation,
          transformation, error-correct, dialogue, build-question — are
          temporarily hidden.
        </p>
        <p>
          This is a calibration phase, not a permanent state. The reason is that
          the FSRS memory model needs <em>clean Bernoulli data</em> (binary
          right/wrong with a well-defined chance baseline) to fit its 19
          parameters honestly. Free-text answers grade as binary today but carry
          partial-credit information that we throw away — a typo
          ("Wexselwirkung" vs "Wechselwirkung") looks the same as "completely
          forgotten" to the current grader, which mis-calibrates everything
          downstream. The 2026-05-25 calibration audit on prod data confirmed
          this: free-input Brier was 0.43 vs 0.19 on multiple-choice.
        </p>
        <p>
          While we're in this mode, every <code>fill_blank</code> and{" "}
          <code>paradigm</code> item is converted to an MC at dispatch — its
          options are synthesised on the fly from the same skill's other
          expected answers, deterministic by exercise id so positions are
          stable. Once the recalibrated weights land and the embedding-based
          continuous grader for free text ships, the banner comes down and the
          rest of the formats come back.
        </p>
        <p className="text-ink-500 text-sm">
          The next section — <em>Glossary of variables</em> — defines K, P, g,
          S, and D, the symbols the bar and tables use.
        </p>
      </Section>

      <Section title="Glossary of variables">
        <p>
          Every symbol that appears in the practice header, the popovers, and
          the rest of this page — defined here once, with formulas where any and
          small example tables for sanity-checking.
        </p>

        <GlossaryEntry
          symbol="K"
          name="knowledge above chance"
          units="dimensionless, 0–1"
        >
          <p>
            What the bar on the practice screen shows. The zero-end is{" "}
            <em>not</em> "you'll always be wrong" — it is{" "}
            <em>maximum entropy</em>, i.e. you'd do exactly what random guessing
            gives on this format. The model never claims you'd do <em>worse</em>{" "}
            than guessing on the displayed scale.
          </p>
          <pre className="text-sm bg-ink-50 p-3 rounded border border-ink-100">
            {`K = max(0, (P − g) / (1 − g))`}
          </pre>
          <GlossaryTable
            headers={["format", "g", "P", "K", "reading"]}
            rows={[
              ["4-option MC", "0.25", "0.25", "0.00", "guessing — no info"],
              ["4-option MC", "0.25", "0.50", "0.33", "some knowledge"],
              ["4-option MC", "0.25", "1.00", "1.00", "you've got this"],
              ["true / false", "0.50", "0.50", "0.00", "coin flip"],
              ["free input", "0.00", "0.30", "0.30", "partial knowledge"],
              ["free input", "0.00", "0.95", "0.95", "near-certain knowledge"],
            ]}
          />
        </GlossaryEntry>

        <GlossaryEntry
          symbol="P"
          name="raw probability you'd get it right"
          units="probability, 0–1"
        >
          <p>
            The memory model's underlying probability that you would produce the
            correct response right now, before any adjustment for what kind of
            question is about to be asked. It does not know the format you're
            about to see — that's what <code>g</code> corrects for. Decays with
            time since last review, controlled by <code>S</code>. The internal
            FSRS code calls this number <code>retrievability</code>; the
            user-facing surface uses <code>P</code> because the FSRS jargon
            collides with the classifier-recall meaning.
          </p>
          <pre className="text-sm bg-ink-50 p-3 rounded border border-ink-100">
            {`P(Δt, S) = 1 / (1 + Δt / (9·S))`}
          </pre>
          <GlossaryTable
            headers={["S (days)", "Δt (days)", "P"]}
            rows={[
              ["1", "1", "0.90"],
              ["1", "7", "0.56"],
              ["10", "1", "0.99"],
              ["10", "7", "0.93"],
              ["10", "90", "0.50"],
              ["100", "90", "0.91"],
            ]}
          />
        </GlossaryEntry>

        <GlossaryEntry
          symbol="g"
          name="guess rate (max-entropy answer policy)"
          units="probability, 0–1"
        >
          <p>
            The probability of a correct response from a learner who knows
            nothing about this specific item and samples answers under the{" "}
            <strong>maximum-entropy answer policy</strong> — uniform over the
            format's outcome space. The max-entropy distribution lives over the{" "}
            <em>answers</em>; <code>g</code> is the scalar that distribution
            induces on "P(correct)". Fixed per format, not per skill.
          </p>
          <p className="text-ink-500">
            For free text the answer space is combinatorial, so{" "}
            <code>g ≈ 0</code> is a pragmatic approximation rather than an exact
            derivation. The cleaner long-term move (once free-text grading is
            continuous) is to measure <code>g</code> empirically as the expected
            grade under a no-information output policy.
          </p>
          <GlossaryTable
            headers={["format", "g", "max-entropy policy"]}
            rows={[
              [
                "free input / typed answer",
                "≈ 0.00",
                "uniform over text strings — combinatorial; ~zero hit rate",
              ],
              ["true / false", "0.50", "uniform over {right, wrong}"],
              ["4-option multiple choice", "0.25", "uniform over 4 options"],
              ["k-option multiple choice", "1/k", "uniform over k options"],
            ]}
          />
        </GlossaryEntry>

        <GlossaryEntry symbol="S" name="stability" units="days">
          <p>
            Per-skill, per-learner number describing how durable that memory is.
            Operationally:{" "}
            <strong>
              the number of days from your last review at which <code>P</code>{" "}
              drops to 90%.
            </strong>{" "}
            Bigger S = the memory decays more slowly. Correct after a real wait
            grows S; wrong shrinks it. Same-session re-answers are
            short-circuited so they don't get credited as genuine spaced
            repetition.
          </p>
          <GlossaryTable
            headers={[
              "S",
              "P after 1 day",
              "P after 1 week",
              "P after 1 month",
            ]}
            rows={[
              ["1 day", "0.90", "0.56", "0.23"],
              ["7 days", "0.98", "0.90", "0.68"],
              ["30 days", "1.00", "0.97", "0.90"],
              ["365 days", "1.00", "1.00", "0.99"],
            ]}
          />
        </GlossaryEntry>

        <GlossaryEntry
          symbol="Δt"
          name="elapsed days since last review"
          units="days, ≥ 0"
        >
          <p>
            Measured, not predicted. Zero on a same-session re-answer. Counted
            in fractional days from the previous <code>finished_at</code>{" "}
            timestamp.
          </p>
        </GlossaryEntry>

        <GlossaryEntry symbol="D" name="difficulty" units="[1, 10]">
          <p>
            Per-skill, per-learner number describing how intrinsically hard the
            skill is for you. Does <em>not</em> appear in today's <code>P</code>
            . It controls how <code>S</code> evolves on the next review — a
            high-D skill grows S slowly even when you get it right; a low-D
            skill compounds faster. Initialised from your first grade and drifts
            toward a per-population mean over time.
          </p>
        </GlossaryEntry>
      </Section>

      <Section title="What the numbers on the screen mean">
        <ul>
          <li>
            <strong>the bar</strong> — your knowledge above chance (
            <code>K</code>) for the primary skill of the current exercise. Empty
            = same as random guessing; full = certain knowledge. See the
            glossary above for the formula.
          </li>
          <li>
            <strong>S Yd</strong> — stability, in days. Operationally: the
            number of days from your last review at which <code>P</code> (the
            underlying memory probability) drops to 90%.
          </li>
          <li>
            <strong>next in Z</strong> — when the scheduler will resurface this
            skill (when <code>P</code> is predicted to hit 85%). Always{" "}
            <code>≈ 1.59 × S</code>.
          </li>
          <li>
            <strong>X bits</strong> — Shannon surprise of your attempt. High
            bits means the model assigned low probability to the outcome you
            produced; it just learned a lot about you.
          </li>
          <li>
            <strong>retention</strong> — net change in stability across every
            skill+word the attempt touched, in days.
          </li>
        </ul>
      </Section>

      <Section title="The math (for the curious)">
        <p>
          The model is FSRS-5. The forgetting curve is{" "}
          <code>P(Δt, S) = 1 / (1 + Δt / (9·S))</code>; the update rules for S
          after a right or wrong answer are calibrated 19-parameter functions of
          (S, D, P, grade) — see <code>services/memory.py</code> (where the
          variable still lives under its FSRS name, <code>retrievability</code>
          ). The scheduler is currently a myopic argmax over information-gain
          bits per candidate exercise, gated by overdue-ness — see{" "}
          <code>services/scheduler.py</code>. The planner and Bellman rollouts
          (referenced below) are experimental and not on the default path.
        </p>
      </Section>

      <Section title="The technical version">
        <p>
          The engine is framed as a{" "}
          <strong>partially observable Markov decision process</strong>. The
          system never sees what you actually know; it only sees your answers.
          From those answers it maintains a<em> belief</em> — a continuous
          memory trace per skill (stability in days, intrinsic difficulty). A
          separate <em>agent</em> reads the belief and picks the next action —
          which skill, which specific exercise, at which scaffolding level — by
          estimating, for every candidate, how much learning value a rollout
          into the future would yield. That estimate is the{" "}
          <strong>Q-value</strong>; the winner's Q-value is the{" "}
          <strong>V-value</strong> of the current belief.
        </p>
        <p>
          Every attempt writes a new row of data: what was shown, how long you
          took, whether you got it right, and{" "}
          <em>five different candidate reward signals</em> computed from that
          outcome. The agent today optimises one of the five — information bits,
          explained below. The other four are logged alongside so the choice of
          reward can be settled empirically when enough data has accumulated,
          rather than by argument.
        </p>
      </Section>

      <Section title="Two separate systems: the agent and the judge">
        <p>
          It is worth saying out loud that there are two distinct systems inside
          one app:
        </p>
        <ul>
          <li>
            <strong>The agent.</strong> Reads the belief about you. Chooses the
            next exercise and scaffolding level. Does not touch your responses,
            does not decide if you were right, does not update your memory
            trace. Lives in <code>services/scheduler.py</code> and{" "}
            <code>services/planner.py</code>.
          </li>
          <li>
            <strong>The judge.</strong> Observes your response. Decides correct
            or wrong. Updates the memory trace (stability, difficulty, next-due
            date). Computes the per-attempt rewards. Lives in{" "}
            <code>services/grader.py</code> and{" "}
            <code>services/evaluator.py</code>.
          </li>
        </ul>
        <p>
          They only communicate through the belief and the reward. Swapping the
          reward function touches only the judge. Swapping the planner touches
          only the agent. Keeping them separate is deliberate — the reward
          function is the most load-bearing normative choice in the whole
          system, and we want it to be a single line we can change without
          rewiring anything else.
        </p>
      </Section>

      <Section title="Belief: a continuous memory trace">
        <p>
          For every skill (e.g. <code>de.gram.case.accusative</code>), the
          system maintains two numbers describing your memory of that skill: a{" "}
          <strong>stability</strong> <code>S</code> measured in days (how long
          the memory will last before you start forgetting), and an{" "}
          <strong>intrinsic difficulty</strong> <code>D</code> in the range{" "}
          <code>[1, 10]</code>. Given the time since your last review{" "}
          <code>Δt</code>, the raw probability you'd produce the correct
          response (the variable we display as <code>P</code> and the internal
          code still calls <code>retrievability</code>) is:
        </p>
        <pre className="text-sm bg-ink-50 p-3 rounded border border-ink-100">{`P(Δt) = (1 + Δt / (9·S))⁻¹`}</pre>
        <p>
          Each attempt updates <code>(S, D)</code> via the Free Spaced
          Repetition Scheduler, version 5 — the same algorithm that powers
          Anki's modern scheduler. Its 19 parameters are calibrated from
          millions of Anki reviews. A correct answer after a real interval grows
          stability; a wrong answer shrinks it; a correct answer five minutes
          after the last one does almost nothing — it carries no information.
          That last property is the whole point. The earlier engine (Bayesian
          Knowledge Tracing, Corbett & Anderson 1995) was time-blind — it
          couldn't distinguish "I got this right five minutes after I last saw
          it" from "I got this right after a week away" — which led to inflated
          mastery numbers and an attractor floor at around 11% even for learners
          who always failed.
        </p>
      </Section>

      <Section title="Observation: per-exercise item calibration">
        <p>
          Each exercise carries two parameters from Item Response Theory: a{" "}
          <strong>discrimination</strong> <code>a</code> (how sharply the item
          separates strong learners from weak ones) and a{" "}
          <strong>difficulty</strong> <code>b</code> (the ability level at which
          a learner has a 50% chance of getting it right). Your predicted
          probability of a correct response is the two-parameter logistic:
        </p>
        <pre className="text-sm bg-ink-50 p-3 rounded border border-ink-100">{`p_item = σ(a · (θ − b))`}</pre>
        <p>
          where <code>θ</code> is your current ability on the skill, derived
          from <code>P</code>. Every attempt does one stochastic-gradient step
          on the item's <code>(a, b)</code>, so exercises learn which ones are
          actually hard in practice — not which ones the author guessed were
          hard when seeding the content.
        </p>
      </Section>

      <Section title="The action space">
        <p>
          An action is a triple:{" "}
          <strong>
            which skill, which specific exercise, at which scaffolding level
          </strong>
          . There are four scaffolding levels per exercise —
        </p>
        <ul>
          <li>
            <code>0</code> — options and hint shown (easiest)
          </li>
          <li>
            <code>1</code> — multiple-choice options only
          </li>
          <li>
            <code>2</code> — hint only (you have to type)
          </li>
          <li>
            <code>3</code> — cold typing, no help at all
          </li>
        </ul>
        <p>
          Crucially, scaffolding is not a deterministic function of how well you
          know the skill. It's a <em>choice</em> the agent makes. The same
          exercise shown cold vs. with options has different predicted outcome
          probabilities (options add a 1-in-4 guess floor; a hint shifts the
          effective difficulty down) — and therefore different expected learning
          payoffs. The agent picks the scaffolding level that maximises expected
          learning, per the reward function.
        </p>
        <p>
          With roughly a hundred eligible skills and four scaffolding levels,
          the agent evaluates on the order of four hundred actions on every
          pick.
        </p>
      </Section>

      <Section title="Policy: the Bellman planner">
        <p>
          For each candidate action the agent estimates its{" "}
          <strong>Q-value</strong> <code>Q̂(b, a)</code> by Monte Carlo rollouts
          under the environment model we trust — Free Spaced Repetition
          Scheduler for the memory dynamics, Item Response Theory for the
          observation. A single rollout looks like this, five steps deep:
        </p>
        <ol>
          <li>
            Commit to action <code>a</code>.
          </li>
          <li>
            Sample an outcome (correct or wrong) from the predicted
            <code>P(correct)</code>.
          </li>
          <li>Apply the memory-trace update under that sampled outcome.</li>
          <li>Collect the reward (in bits; see below).</li>
          <li>Advance simulated time by a day.</li>
          <li>Let the rollout policy pick the next action and loop.</li>
        </ol>
        <p>
          We average 24 such trajectories per candidate, discount with{" "}
          <code>γ = 0.95</code>, and take the mean cumulative reward as the
          Q-value estimate. The action with the highest Q-value wins; that
          Q-value is also the <strong>V-value</strong> of being in the current
          belief state, <code>V̂(b) = max_a Q̂(b, a)</code>.
        </p>
        <p>
          <strong>Overdue override.</strong> Any skill whose scheduled review
          date is more than a day in the past jumps the queue and is served
          cold, regardless of Q-value. That's the spaced- repetition backbone —
          forgetting is a stronger signal than any planner can override.
        </p>
        <p>
          Turn on the <em>Scheduler debug overlay</em> flag to see the action
          space the agent actually weighed on any given pick, with every
          candidate's Q-value and the winner marked. It's the clearest view of
          what the agent is thinking.
        </p>
      </Section>

      <Section title="A quick detour — what is a bit?">
        <p>
          The reward function is measured in bits, so it's worth explaining what
          that means in its own right.
        </p>
        <p>
          A <strong>bit of information</strong> is the uncertainty reduction
          from one yes/no question whose two outcomes were equally likely
          beforehand. Flip a fair coin, see "heads" — you've learned exactly one
          bit. More formally, if an outcome had prior probability <code>p</code>{" "}
          and you observe it, it carries:
        </p>
        <pre className="text-sm bg-ink-50 p-3 rounded border border-ink-100">{`r = −log₂(p)   bits`}</pre>
        <ul>
          <li>
            <code>p = 0.5</code> (fair coin) → 1 bit.
          </li>
          <li>
            <code>p = 0.99</code> (you were almost certain) → 0.014 bits.
          </li>
          <li>
            <code>p = 0.05</code> (very surprising) → 4.32 bits.
          </li>
        </ul>
        <p>
          The logarithm makes independent observations add cleanly: observing
          two things with joint probability <code>p₁·p₂</code>
          carries <code>−log₂(p₁·p₂) = −log₂(p₁) + −log₂(p₂)</code> bits. The
          minus sign flips the negative log into a non-negative number that
          grows as the observation gets more surprising.
        </p>
      </Section>

      <Section title="Reward: one number drives the agent; five are logged">
        <p>
          The agent today optimises one reward: the{" "}
          <strong>information bits</strong> the observation carried under the
          agent's prediction —{" "}
          <code>r = −log₂ P(observed | belief, action)</code>. This peaks when
          the agent's predicted
          <code> P(correct) = 0.5</code>, which means:
        </p>
        <ul>
          <li>
            A trivially easy question to you yields near-zero bits. No reward
            for grinding a known item.
          </li>
          <li>
            A question you would certainly fail also yields near-zero bits — the
            outcome confirmed what the agent already expected.
          </li>
          <li>
            A question at the boundary of what you can do — the "zone of
            proximal development" — yields maximal bits. Whichever way it lands,
            the observation told us something.
          </li>
        </ul>
        <p>
          This is the mechanism that makes scaffolding a real choice. The agent
          doesn't just pick the easiest version of a question; it picks the one
          whose outcome is most informative. Showing a hint to a learner who
          already knows the skill earns near-zero bits (trivially certain
          success); cold-typing a skill the learner is hopeless on also earns
          near-zero bits (trivially certain failure). The scaffolding choice is
          selected when it moves the predicted probability <em>toward</em> 0.5.
        </p>
        <p>
          Information bits is a defensible reward — Lindley's 1956 result on
          Bayesian experimental design proves it is optimal for{" "}
          <em>estimating</em> the learner's state. It is <em>not</em>
          automatically optimal for <em>teaching</em>. A pure-entropy reward
          could keep a learner permanently oscillating near <code>P = 0.5</code>{" "}
          without ever building durable memory. To avoid committing to the wrong
          objective, the judge logs <strong>five candidate rewards</strong> on
          every attempt:
        </p>
        <ul>
          <li>
            <code>information_gain_bits</code> — the current reward the agent
            optimises.
          </li>
          <li>
            <code>retention_time_gained_days</code> — how much stability the
            attempt added (or, if wrong, removed). The Duolingo
            half-life-regression and the MEMORIZE literature optimise something
            like this.
          </li>
          <li>
            <code>learning_progress_delta</code> — the change in your rolling
            accuracy on this skill over a sliding window. The Oudeyer-Lopes
            ZPDES signal.
          </li>
          <li>
            <code>posterior_variance_reduction</code> — a Tabibian-style proxy
            for how much the observation sharpened our uncertainty about your{" "}
            <code>P</code>.
          </li>
          <li>
            <code>mastery_threshold_crossed</code> — a coarse boolean: did this
            attempt push <code>P</code> over 70% for the first time? Matches the
            Goethe-style product metric.
          </li>
        </ul>
        <p>
          Once enough attempts accumulate, we can correlate each candidate
          reward against a concrete downstream outcome — for instance,
          <code>P</code> on the same skill seven days later — and pick the
          reward that actually predicts retention. Until then, the choice of
          information bits is a reasonable prior, not a permanent commitment.
        </p>
      </Section>

      <Section title="Scaffolded grading: HARD vs GOOD">
        <p>
          An attempt's outcome affects the memory trace via the underlying FSRS
          grade. We use two:
        </p>
        <ul>
          <li>
            <code>GOOD</code> — correct on a cold attempt (no help shown). Full
            stability bump.
          </li>
          <li>
            <code>HARD</code> — correct on a scaffolded attempt (hint or options
            were shown). Smaller stability bump.
          </li>
          <li>
            <code>AGAIN</code> — wrong answer, any scaffolding. Lapse.
          </li>
        </ul>
        <p>
          In other words: getting a question right with a hint counts, but it
          counts less than getting it right cold. This prevents the belief from
          being inflated by assisted answers — a real, unassisted retrieval is
          stronger evidence of long-term memory than a hint-walked one, and the
          stability update reflects that.
        </p>
      </Section>

      <Section title="Grading">
        <p>
          For structured answers (multiple choice, single-word fill-in), grading
          is exact-match with a few humane normalizations:
        </p>
        <ul>
          <li>Case and whitespace don't matter.</li>
          <li>
            Trailing punctuation (<code>.?!,;:</code>) doesn't count — "Ich bin
            müde" == "Ich bin müde."
          </li>
          <li>
            Missing umlauts / ß get accepted with a note showing the canonical
            spelling: "grun" → correct, but you'll see that the real form is
            "grün". Toggle <strong>Strict umlauts</strong> in the flags menu to
            disable.
          </li>
        </ul>
        <p>
          Free-text translation and writing will eventually route to an LLM
          judge. That's future work — for now, full-sentence prompts still rely
          on exact match against a canonical answer.
        </p>
      </Section>

      <Section title="How vocabulary is counted">
        <p>
          The "words known" number on the home page is intentionally
          conservative. A word counts only if:
        </p>
        <ol>
          <li>It is the expected answer on a vocabulary exercise, and</li>
          <li>
            You have attempted that specific exercise at least once and gotten
            it correct, and
          </li>
          <li>
            Your current <code>P</code> on the underlying skill is at least 70%.
          </li>
        </ol>
        <p>
          No double-counting, no marking-unmarked-as-known inflation. The
          estimated text coverage percentage on the home page is a Zipfian
          heuristic: natural-language vocabulary follows a power law, so the
          first 100 words cover roughly half of a typical text, 500 cover about
          75%, 2000 cover about 92%. The counter maps your known-words total
          onto that curve.
        </p>
      </Section>

      <Section title="What this ISN'T (yet)">
        <ul>
          <li>
            <strong>Not model-free reinforcement learning.</strong> We do have a
            Q-function and a V-function — both estimated online per pick — but
            those estimates come from Monte Carlo rollouts through a{" "}
            <em>hand-specified</em> environment model (Free Spaced Repetition
            Scheduler transitions, Item Response Theory observations). If our
            model of the learner is wrong, the Q-values are wrong. Validated
            model-based planning, not deep reinforcement learning.
          </li>
          <li>
            <strong>Not an empirically-chosen reward.</strong> The agent
            optimises information bits. That's a prior, not a conclusion. Five
            candidate rewards are logged alongside so the choice can be settled
            by data rather than by argument.
          </li>
          <li>
            <strong>No audio yet.</strong> Pronunciation, listening
            comprehension, and speaking assessment are planned for a later
            phase. Whisper is insufficient for fine-grained pronunciation
            scoring; the plan is wav2vec2 phoneme posteriors and forced
            alignment. See <code>docs/open-problems.md</code>.
          </li>
          <li>
            <strong>No large-language-model grading yet.</strong>{" "}
            Claude-as-judge will land for translation and writing exercises
            where exact-match grading is too strict.
          </li>
          <li>
            <strong>Not a replacement for real-world practice.</strong> You live
            in Germany. Talking to people, reading signs, understanding S-Bahn
            announcements — that is the ground truth. This app is a scaffolding
            tool, not a substitute.
          </li>
        </ul>
      </Section>

      <footer className="mt-16 pt-8 border-t border-ink-100 text-ink-400 text-sm">
        <p>
          Deeper reading in the repo: <code>docs/learning-theory.md</code>,{" "}
          <code>docs/rl-framing.md</code>, <code>docs/open-problems.md</code>,{" "}
          <code>docs/roadmap.md</code>.
        </p>
        <p className="mt-2">
          <Link
            to="/practice"
            className="group inline-flex items-center gap-1 text-ink-700 hover:text-ink-900 transition-colors"
          >
            <span>Back to practice</span>
            <span
              aria-hidden
              className="transition-transform duration-200 group-hover:translate-x-0.5"
            >
              →
            </span>
          </Link>
        </p>
      </footer>
    </article>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  // h2 carries the section's roman numeral via a CSS ::before
  // counter declared in index.css under `.essay`. No prop drilling
  // needed — the article scopes the counter and each h2 auto-increments.
  return (
    <section className="mb-10 essay-section">
      <h2 className="font-serif text-2xl text-ink-900 mb-3 tracking-tight">
        {title}
      </h2>
      <div className="text-ink-700 space-y-3 leading-relaxed">{children}</div>
    </section>
  );
}

function GlossaryEntry({
  symbol,
  name,
  units,
  children,
}: {
  symbol: string;
  name: string;
  units: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-l-2 border-ink-200 pl-4 my-5 space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0">
        <code className="font-serif text-2xl text-ink-900 not-italic">
          {symbol}
        </code>
        <span className="text-ink-900 font-medium">{name}</span>
        <span className="text-xs text-ink-400">{units}</span>
      </div>
      {children}
    </div>
  );
}

function GlossaryTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="text-sm border border-ink-200 my-2">
        <thead className="bg-ink-50">
          <tr>
            {headers.map((h) => (
              <th
                key={h}
                className="px-2 py-1 text-left font-medium text-ink-700 border-b border-ink-200"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-ink-100 last:border-0">
              {row.map((cell, j) => (
                <td
                  key={j}
                  className="px-2 py-1 text-ink-700 tabular-nums whitespace-nowrap"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
